'use client';
import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';

interface Service {
  id: number;
  title: string;
  description: string;
  details: string;
  image: string;
  imageAlt: string;
  dark: boolean;
  href: string;
  icon: React.ReactNode;
}

const services: Service[] = [
{
  id: 3,
  title: 'Commercial Real Estate Finance',
  description: 'Specialized financing solutions for commercial real estate acquisitions, development, and refinancing.',
  details:
  'Grid Global provides expert commercial real estate financing services for investors, developers, and business owners. We facilitate financing for office buildings, retail centers, industrial properties, multifamily complexes, and mixed-use developments. Our team structures deals that maximize leverage while minimizing risk, working with a broad network of commercial real estate lenders to find the best terms for your project.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_11dcdef04-1786980765868.png",
  imageAlt: 'Modern commercial real estate building representing Grid Global real estate finance services',
  dark: false,
  href: '/services/commercial-real-estate-finance',
  icon:
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>

},
{
  id: 2,
  title: 'Asset-Based Lending',
  description: 'Unlock the capital in your receivables, inventory, and equipment with flexible asset-backed credit facilities.',
  details:
  'Grid Global structures asset-based lending facilities that convert your balance sheet into working capital. Whether you need a revolving line of credit backed by receivables, an inventory facility, or equipment financing, we identify the right lender and negotiate advance rates and covenants that work for your business cycle. Ideal for asset-intensive businesses seeking growth capital without diluting equity.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1571bbea4-1772185088461.png",
  imageAlt: 'Financial documents and calculator on desk representing asset-based lending services',
  dark: true,
  href: '/services/asset-based-lending',
  icon:
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>

},
{
  id: 1,
  title: 'Financial Advisory Services',
  description: 'Strategic financial consulting to optimize your business capital structure and growth trajectory.',
  details:
  'Our financial advisory team provides comprehensive consulting services including capital structure optimization, merger and acquisition advisory, financial restructuring, and strategic planning. We work closely with business owners and executives to develop actionable financial strategies that align with your long-term objectives. Our advisors bring decades of experience across multiple industries and financial disciplines.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_159556e32-1781075241341.png",
  imageAlt: 'Professional financial advisor in meeting discussing strategy with client',
  dark: false,
  href: '/services/financial-advisory',
  icon:
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>

},
{
  id: 4,
  title: 'Business Loan Management',
  description: 'End-to-end loan management services ensuring smooth processing from application to closing.',
  details:
  'Our dedicated loan management team oversees every aspect of the commercial lending process, from initial application and underwriting support to documentation, compliance review, and closing coordination. We act as your advocate throughout the entire process, managing lender relationships, resolving issues proactively, and ensuring timely closings. Our systematic approach reduces delays and maximizes approval rates for our clients.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_12afa447e-1773079079858.png",
  imageAlt: 'Business professionals reviewing loan documents and financial paperwork',
  dark: true,
  href: '/services/business-loan-management',
  icon:
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>

}];


function ServiceCard({ service, index }: {service: Service;index: number;}) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) setVisible(true);},
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const isEven = index % 2 === 0;

  return (
    <div
      ref={ref}
      className={`w-full py-12 md:py-16 ${
      service.dark ?
      'bg-brand-darkbg' : 'bg-white'}`
      }>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div
          className={`grid lg:grid-cols-2 gap-8 md:gap-12 items-center ${
          !isEven ? 'lg:grid-flow-dense' : ''}`
          }>
          
          {/* Image */}
          <div
            className={`${
            !isEven ? 'lg:col-start-2' : ''} transition-all duration-700 ${

            visible ?
            'opacity-100 translate-x-0' :
            isEven ?
            'opacity-0 -translate-x-10' : 'opacity-0 translate-x-10'}`
            }>
            
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
              <Image
                src={service.image}
                alt={service.imageAlt}
                width={700}
                height={450}
                className="w-full h-56 sm:h-64 md:h-72 object-cover transition-transform duration-500 group-hover:scale-105" />
              
              <div
                className={`absolute inset-0 ${
                service.dark ?
                'bg-gradient-to-br from-brand-navy/40 to-transparent' : 'bg-gradient-to-br from-brand-navy/20 to-transparent'}`
                } />
              
              {/* Icon badge */}
              <div className="absolute top-4 left-4 w-12 h-12 md:w-14 md:h-14 rounded-xl bg-brand-green/90 backdrop-blur-sm flex items-center justify-center text-white shadow-lg">
                {service.icon}
              </div>
            </div>
          </div>

          {/* Text */}
          <div
            className={`${
            !isEven ? 'lg:col-start-1 lg:row-start-1' : ''} transition-all duration-700 delay-200 ${

            visible ?
            'opacity-100 translate-x-0' :
            isEven ?
            'opacity-0 translate-x-10' : 'opacity-0 -translate-x-10'}`
            }>
            
            <div className={service.dark ? 'text-white' : 'text-brand-navy'}>
              <h2
                className={`font-serif text-2xl sm:text-3xl md:text-4xl font-medium mb-4 ${
                service.dark ? 'text-white' : 'text-brand-navy'}`
                }>
                
                {service.title}
              </h2>
              <p
                className={`font-sans text-base md:text-lg font-medium mb-4 ${
                service.dark ? 'text-brand-lightblue' : 'text-brand-blue'}`
                }>
                
                {service.description}
              </p>
              <p
                className={`font-sans text-sm md:text-base leading-relaxed ${
                service.dark ? 'text-gray-400' : 'text-gray-600'}`
                }>
                
                {service.details}
              </p>
              <div className="mt-6 md:mt-8">
                <Link
                  href={service.href}
                  className={`inline-flex items-center gap-2 px-5 md:px-6 py-3 rounded-md font-sans font-semibold text-sm transition-all duration-200 hover:-translate-y-0.5 ${
                  service.dark ?
                  'bg-brand-green text-white hover:bg-green-600 hover:shadow-lg hover:shadow-brand-green/30' :
                  'bg-brand-navy text-white hover:bg-brand-blue hover:shadow-lg hover:shadow-brand-navy/30'}`
                  }>
                  
                  Learn More
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>);

}

export default function Services() {
  return (
    <section id="services">
      {/* Section Header */}
      <div
        className="w-full pt-20 pb-8 relative"
        style={{
          backgroundImage: "url('/assets/images/jeffrey-blum-7-gaPkhIgqs-unsplash-1786980329175.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}>
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-brand-navy/70" />
        <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
          <h2 className="font-serif text-4xl md:text-5xl font-medium text-white mb-4">
            Our Services
          </h2>
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-16 bg-brand-green" />
            <div className="w-2 h-2 rounded-full bg-brand-green" />
            <div className="h-px w-16 bg-brand-green" />
          </div>
          <p className="font-sans text-gray-300 text-lg max-w-2xl mx-auto">
            Comprehensive financial brokerage and advisory services tailored to your business needs
          </p>
        </div>
      </div>

      {/* Service Cards - Alternating */}
      {services.map((service, index) =>
      <ServiceCard key={service.id} service={service} index={index} />
      )}
    </section>);

}